import React from 'react';
import { z } from 'zod';
import type { BlockDefinition } from '../types';

/**
 * Shared lead-capture form — ONE component reused across every tenant for
 * consistency. Class-based markup only (no inline styles / styled-components) so
 * it's legal on tenants that forbid third-party assets; styled by the same-origin
 * /styles/lead-form.css, theme-adaptive via CSS variables. Submits through the
 * delegated `form[data-lead-form]` handler in apps/web (validation + hygiene +
 * POST /api/leads + open-Calendly-on-success), so behaviour is identical everywhere.
 */

// Standard mandatory fields (identical across all tenants).
const FIELDS: { name: string; label: string; type: string }[] = [
  { name: 'firstName', label: 'First name', type: 'text' },
  { name: 'lastName', label: 'Last name', type: 'text' },
  { name: 'title', label: 'Job title', type: 'text' },
  { name: 'company', label: 'Company name', type: 'text' },
  { name: 'email', label: 'Work email', type: 'email' },
  { name: 'phone', label: 'Phone', type: 'tel' },
];

export const leadFormSchema = z.object({
  heading: z.string().default('Get in touch'),
  subheading: z.string().default('Tell us what you need — we’ll be in touch shortly.'),
  submitLabel: z.string().default('Submit'),
  messageLabel: z.string().default('Message'),
  // When on (and the tenant has a Calendly URL configured), a successful submit
  // opens the Calendly scheduling popup. Turn off to keep the form standalone.
  openCalendly: z.boolean().default(true),
});

/** The reusable form element (also embeddable inside other components, e.g. a modal). */
export function LeadFormFields(p: { submitLabel: string; messageLabel: string; openCalendly?: boolean }) {
  // Default on: preserve the existing "form → schedule" flow. Only an explicit
  // `false` opts a form out of opening Calendly after submit.
  const calendlyOff = p.openCalendly === false;
  return (
    <>
    {/* Same-origin stylesheet — loads wherever the shared form is embedded (legal on
        no-third-party tenants like Kathreftis). Browsers dedupe repeated links. */}
    <link rel="stylesheet" href="/styles/lead-form.css" />
    <form
      className="lf"
      method="post"
      action="/api/leads"
      data-lead-form
      {...(calendlyOff ? { 'data-lead-calendly': 'off' } : {})}
      noValidate
    >
      <input className="lf__hp" type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      {FIELDS.map((f) => (
        <div className="lf__field" key={f.name}>
          <label className="lf__label" htmlFor={`lf-${f.name}`}>{f.label}*</label>
          <input className="lf__input" id={`lf-${f.name}`} name={f.name} type={f.type} required aria-label={f.label} />
          {/* Per-field validation message — filled by the delegated handler. */}
          <p className="lf__err" data-lead-error={f.name} aria-live="polite" />
        </div>
      ))}
      <div className="lf__field lf__span">
        <label className="lf__label" htmlFor="lf-message">{p.messageLabel}*</label>
        <textarea className="lf__textarea" id="lf-message" name="message" required placeholder="Min 40 characters" />
        <p className="lf__err" data-lead-error="message" aria-live="polite" />
      </div>
      <p className="lf__status" data-lead-status aria-live="polite" />
      <button type="submit" className="lf__btn">{p.submitLabel}</button>
    </form>
    </>
  );
}

function LeadForm(p: z.infer<typeof leadFormSchema>) {
  return (
    <section className="lf-block">
      <link rel="stylesheet" href="/styles/lead-form.css" />
      {p.heading ? <h2 className="lf-block__title">{p.heading}</h2> : null}
      {p.subheading ? <p className="lf-block__sub">{p.subheading}</p> : null}
      <LeadFormFields submitLabel={p.submitLabel} messageLabel={p.messageLabel} openCalendly={p.openCalendly} />
    </section>
  );
}

// Block type is `contactForm` so it reads "Contact Form" in the section picker
// (findable by searching "contact"); the shared component keeps the leadForm* names.
export const leadFormBlock: BlockDefinition = { type: 'contactForm', schema: leadFormSchema, Renderer: LeadForm };
